import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Newspaper, ExternalLink, Loader2, RefreshCw, Search, Plus, Trash2, Pencil, Share2, Heart, MessageCircle, Facebook, Twitter, Linkedin, Mail, X } from 'lucide-react';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { newsAPI } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { getCategoryBadgeClass, getCategoryTabClasses } from '../constants/newsCategoryColors';

const NEWS_CATEGORY_IDS = ['all', 'equipment_materials', 'research_development', 'finance_economic', 'weather', 'others'] as const;

const t = (key: string, fallback: string) => fallback;

const NewsPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => selectUser(state));
  
  const [actualities, setActualities] = useState<any[]>([]);
  const [allActualities, setAllActualities] = useState<any[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState<string | null>(null);

  const [communityNews, setCommunityNews] = useState<any[]>([]);
  const [allCommunityNews, setAllCommunityNews] = useState<any[]>([]);
  const [communityLoading, setCommunityLoading] = useState(true);
  const [communityError, setCommunityError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [newsSourceTab, setNewsSourceTab] = useState<'all' | 'community' | 'google'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [deletingNewsId, setDeletingNewsId] = useState<string | null>(null);
  const [shareMenuOpen, setShareMenuOpen] = useState<string | null>(null);
  const [articleEngagement, setArticleEngagement] = useState<Record<string, { likes: any[]; comments: any[]; shareCount: number; userLiked: boolean }>>({});
  const [commentsModalItem, setCommentsModalItem] = useState<any | null>(null);
  const [likingArticleUrl, setLikingArticleUrl] = useState<string | null>(null);
  const [likingNewsId, setLikingNewsId] = useState<string | null>(null);
  const [articleCommentText, setArticleCommentText] = useState('');
  const [submittingArticleComment, setSubmittingArticleComment] = useState(false);

  useEffect(() => {
    if (!shareMenuOpen) return;
    const close = () => setShareMenuOpen(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [shareMenuOpen]);
  
  const [featuredNews, setFeaturedNews] = useState<any[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  const [newsletterSubscribing, setNewsletterSubscribing] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeaturedNews = async () => {
      try {
        setFeaturedLoading(true);
        const response = await newsAPI.getFeaturedNews({ limit: 3 });
        if (response.data.success) {
          setFeaturedNews(response.data.data || []);
        }
      } catch (err: any) {
        console.error('Error fetching featured news:', err);
      } finally {
        setFeaturedLoading(false);
      }
    };
    fetchFeaturedNews();
  }, []);

  useEffect(() => {
    const fetchActualities = async () => {
      try {
        setNewsLoading(true);
        setNewsError(null);
        const response = await newsAPI.getAgricultureNews({ limit: 20 });
        if (response.data.success) {
          const fetchedNews = response.data.data || [];
          setAllActualities(fetchedNews);
          setActualities(fetchedNews);
        } else {
          setNewsError('Failed to load news');
        }
      } catch (err: any) {
        console.error('Error fetching actualities:', err);
        setNewsError(err.response?.data?.message || 'Failed to load news');
        setActualities([]);
      } finally {
        setNewsLoading(false);
      }
    };
    fetchActualities();
  }, []);

  useEffect(() => {
    const fetchCommunityNews = async () => {
      try {
        setCommunityLoading(true);
        setCommunityError(null);
        const res = await newsAPI.getCommunityNews({ limit: 20 });
        if (res.data?.success) {
          const fetchedNews = res.data.data || [];
          setAllCommunityNews(fetchedNews);
          setCommunityNews(fetchedNews);
        } else {
          setCommunityError(res.data?.message || 'Failed to load community news');
          setAllCommunityNews([]);
          setCommunityNews([]);
        }
      } catch (err: any) {
        setCommunityError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load community news');
        setCommunityNews([]);
      } finally {
        setCommunityLoading(false);
      }
    };
    fetchCommunityNews();
  }, []);

  useEffect(() => {
    const urls = actualities
      .map((a) => (a.url && (a.url.startsWith('http') ? a.url : `https://${a.url}`)))
      .filter(Boolean);
    if (urls.length === 0) return;
    newsAPI
      .getArticleEngagement(urls)
      .then((res) => {
        if (res.data?.success && res.data?.data) setArticleEngagement(res.data.data);
      })
      .catch(() => {});
  }, [actualities]);

  const formatTimeOrDate = (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    if (isToday) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear().toString().slice(-2);
    return `${d}.${m}.${y}`;
  };

  const getMergedNewsList = () => {
    const list = [
      ...actualities.map((a) => ({ ...a, _source: 'agriculture' })),
      ...communityNews.map((c) => ({ ...c, _source: 'community' }))
    ];
    list.sort((a, b) => new Date(b.publishedAt || b.createdAt || 0).getTime() - new Date(a.publishedAt || a.createdAt || 0).getTime());
    return list;
  };
  const fullMergedList = getMergedNewsList();
  const mergedList = newsSourceTab === 'all'
    ? fullMergedList
    : newsSourceTab === 'community'
      ? fullMergedList.filter((i) => i._source === 'community')
      : fullMergedList.filter((i) => i._source === 'agriculture');
  const mostReadList = fullMergedList.slice(0, 8);

  const refreshNews = async () => {
    try {
      setNewsLoading(true);
      setNewsError(null);
      const response = await newsAPI.getAgricultureNews({ limit: 20 });
      if (response.data.success) {
        const fetchedNews = response.data.data || [];
        setAllActualities(fetchedNews);
        setActualities(fetchedNews);
      } else {
        setNewsError('Failed to load news');
      }
    } catch (err: any) {
      console.error('Error fetching actualities:', err);
      setNewsError(err.response?.data?.message || 'Failed to load news');
      setActualities([]);
    } finally {
      setNewsLoading(false);
    }
  };

  const handleDeleteNews = async (newsId: string) => {
    if (!window.confirm(t('eventsAndNews.confirmDelete', 'Are you sure you want to delete this news? This action cannot be undone.'))) return;
    setDeletingNewsId(newsId);
    setCommunityError(null);
    try {
      const res = await newsAPI.deleteCommunityNews(newsId);
      if (res.data?.success) {
        setAllCommunityNews((prev) => prev.filter((n) => n._id !== newsId));
        setCommunityNews((prev) => prev.filter((n) => n._id !== newsId));
      } else {
        setCommunityError(res.data?.message || 'Failed to delete news');
      }
    } catch (err: any) {
      console.error('Error deleting news:', err);
      setCommunityError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to delete news');
    } finally {
      setDeletingNewsId(null);
    }
  };

  useEffect(() => {
    let filteredAgriculture = [...allActualities];
    let filteredCommunity = [...allCommunityNews];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredAgriculture = filteredAgriculture.filter(
        (news) =>
          news.title?.toLowerCase().includes(query) ||
          news.description?.toLowerCase().includes(query) ||
          news.author?.toLowerCase().includes(query) ||
          news.source?.toLowerCase().includes(query)
      );
      filteredCommunity = filteredCommunity.filter(
        (news) =>
          news.title?.toLowerCase().includes(query) ||
          news.description?.toLowerCase().includes(query) ||
          news.creator?.name?.toLowerCase().includes(query)
      );
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const filterByDate = (news: any) => {
      const newsDate = news.publishedAt || news.createdAt;
      if (!newsDate) return dateFilter === 'all';
      const date = new Date(newsDate);
      if (dateFilter === 'today') return date >= today;
      if (dateFilter === 'week') return date >= weekAgo;
      if (dateFilter === 'month') return date >= monthAgo;
      return true;
    };

    filteredAgriculture = filteredAgriculture.filter(filterByDate);
    filteredCommunity = filteredCommunity.filter(filterByDate);

    setActualities(filteredAgriculture);
    setCommunityNews(selectedCategory === 'all' ? filteredCommunity : filteredCommunity.filter((n) => (n.category || 'others') === selectedCategory));
  }, [searchQuery, selectedCategory, dateFilter, allActualities, allCommunityNews]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setDateFilter('all');
  };

  const hasActiveFilters = searchQuery.trim() !== '' || selectedCategory !== 'all' || dateFilter !== 'all';

  const handleLikeCommunityNews = async (e: React.MouseEvent, newsId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user?._id || likingNewsId) return;
    setLikingNewsId(newsId);
    try {
      const res = await newsAPI.likeNews(newsId);
      if (res.data?.success && res.data?.data) {
        setCommunityNews((prev) => prev.map((n) => (n._id === newsId ? { ...n, likes: res.data.data.likes || [] } : n)));
        setAllCommunityNews((prev) => prev.map((n) => (n._id === newsId ? { ...n, likes: res.data.data.likes || [] } : n)));
      }
    } catch (err) {
      console.error('Like news error:', err);
    } finally {
      setLikingNewsId(null);
    }
  };

  const handleLikeArticle = async (e: React.MouseEvent, articleUrl: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user?._id || likingArticleUrl) return;
    const url = articleUrl.startsWith('http') ? articleUrl : `https://${articleUrl}`;
    setLikingArticleUrl(url);
    try {
      const res = await newsAPI.likeArticle(url);
      if (res.data?.success && res.data?.data) {
        const eng = res.data.data;
        const userLiked = (eng.likes || []).some((l: any) => String(l.user?._id || l.user) === String(user._id));
        setArticleEngagement((prev) => ({ ...prev, [url]: { likes: eng.likes || [], comments: eng.comments || [], shareCount: eng.shareCount || 0, userLiked } }));
      }
    } catch (err) {
      console.error('Like article error:', err);
    } finally {
      setLikingArticleUrl(null);
    }
  };

  const handleAddArticleComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?._id || !commentsModalItem?.url || !articleCommentText.trim() || submittingArticleComment) return;
    const url = commentsModalItem.url.startsWith('http') ? commentsModalItem.url : `https://${commentsModalItem.url}`;
    setSubmittingArticleComment(true);
    try {
      const res = await newsAPI.addArticleComment(url, articleCommentText.trim());
      if (res.data?.success && res.data?.data) {
        const eng = res.data.data;
        setArticleEngagement((prev) => ({ ...prev, [url]: { ...prev[url], comments: eng.comments || [], likes: eng.likes || [], shareCount: eng.shareCount || 0, userLiked: prev[url]?.userLiked ?? false } }));
        setCommentsModalItem((prev: any) => prev ? { ...prev, comments: eng.comments } : null);
        setArticleCommentText('');
      }
    } catch (err) {
      console.error('Add article comment error:', err);
    } finally {
      setSubmittingArticleComment(false);
    }
  };

  const handleDeleteArticleComment = async (commentId: string) => {
    if (!commentsModalItem?.url) return;
    const url = commentsModalItem.url.startsWith('http') ? commentsModalItem.url : `https://${commentsModalItem.url}`;
    try {
      const res = await newsAPI.deleteArticleComment(url, commentId);
      if (res.data?.success && res.data?.data) {
        const eng = res.data.data;
        setArticleEngagement((prev) => ({ ...prev, [url]: { ...prev[url], comments: eng.comments || [], likes: eng.likes || [], shareCount: eng.shareCount || 0, userLiked: prev[url]?.userLiked ?? false } }));
        setCommentsModalItem((prev: any) => prev ? { ...prev, comments: eng.comments } : null);
      }
    } catch (err) {
      console.error('Delete article comment error:', err);
    }
  };

  const shareNews = (e: React.MouseEvent, news: any, platform: string) => {
    e.preventDefault();
    e.stopPropagation();
    const url = news._source === 'community' && news._id
      ? `${window.location.origin}/news/${news._id}`
      : (news.url || window.location.href);
    const title = news.title;
    const text = news.description?.replace(/<[^>]*>/g, '').substring(0, 200) || '';
    const shareUrl = encodeURIComponent(url);
    const shareTitle = encodeURIComponent(title);
    const shareText = encodeURIComponent(text);

    let shareLink = '';
    switch (platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;
        break;
      case 'email':
        shareLink = `mailto:?subject=${shareTitle}&body=${shareText}%20${shareUrl}`;
        break;
      default:
        if (navigator.share) {
          navigator.share({ title, text, url });
          if (news._source === 'community' && news._id) {
            newsAPI.recordShare(news._id).catch(() => {});
          } else if (news._source === 'agriculture' && news.url) {
            const artUrl = news.url.startsWith('http') ? news.url : `https://${news.url}`;
            newsAPI.recordArticleShare(artUrl).catch(() => {});
        setArticleEngagement((prev) => ({ ...prev, [artUrl]: { ...prev[artUrl], shareCount: (prev[artUrl]?.shareCount || 0) + 1, likes: prev[artUrl]?.likes || [], comments: prev[artUrl]?.comments || [], userLiked: prev[artUrl]?.userLiked ?? false } }));
          }
          setShareMenuOpen(null);
          return;
        }
        shareLink = url;
    }

    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
      if (news._source === 'community' && news._id) {
        newsAPI.recordShare(news._id).catch(() => {});
      } else if (news._source === 'agriculture' && news.url) {
        const artUrl = news.url.startsWith('http') ? news.url : `https://${news.url}`;
        newsAPI.recordArticleShare(artUrl).catch(() => {});
        setArticleEngagement((prev) => ({ ...prev, [artUrl]: { ...prev[artUrl], shareCount: (prev[artUrl]?.shareCount || 0) + 1, likes: prev[artUrl]?.likes || [], comments: prev[artUrl]?.comments || [], userLiked: prev[artUrl]?.userLiked ?? false } }));
      }
      setShareMenuOpen(null);
    }
  };

  const handleNewsletterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) {
      setNewsletterError('Email is required');
      return;
    }
    setNewsletterSubscribing(true);
    setNewsletterError(null);
    setNewsletterSuccess(false);
    try {
      const res = await newsAPI.subscribeNewsletter({
        email: newsletterEmail.trim(),
        name: newsletterName.trim() || undefined
      });
      if (res.data?.success) {
        setNewsletterSuccess(true);
        setNewsletterEmail('');
        setNewsletterName('');
        setTimeout(() => setNewsletterSuccess(false), 5000);
      } else {
        setNewsletterError(res.data?.message || 'Failed to subscribe');
      }
    } catch (err: any) {
      setNewsletterError(err.response?.data?.message || err.message || 'Failed to subscribe');
    } finally {
      setNewsletterSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div className="border-b border-gray-200 pb-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">News</h1>
            {user?._id && (
              <Link
                to="/news/add"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium shrink-0"
              >
                <Plus className="h-4 w-4" />
                Add news
              </Link>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-sm font-medium text-gray-600">Source:</span>
            {(['all', 'community', 'google'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setNewsSourceTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  newsSourceTab === tab ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab === 'all' ? 'All' : tab === 'community' ? 'Community' : 'Google News'}
              </button>
            ))}
          </div>
          {newsSourceTab !== 'google' && (
            <nav className="flex flex-wrap gap-2 mt-3">
              {NEWS_CATEGORY_IDS.map((id) => (
                <button
                  key={id}
                  onClick={() => setSelectedCategory(id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${getCategoryTabClasses(id, selectedCategory === id)}`}
                >
                  {id === 'all' ? 'All categories' : id}
                </button>
              ))}
            </nav>
          )}
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-gray-700">Right now :</span>
            {!featuredLoading && featuredNews.length > 0 ? (
              featuredNews.slice(0, 3).map((news, idx) => (
                <span key={news._id || idx} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                  {news.title?.length > 40 ? news.title.slice(0, 40) + '…' : news.title}
                </span>
              ))
            ) : (
              <>
                <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">Agriculture News</span>
                <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">Community</span>
              </>
            )}
          </div>
          <p className="text-sm text-gray-500">
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-400 focus:border-gray-400 bg-white"
            />
          </div>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as 'all' | 'today' | 'week' | 'month')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gray-400"
          >
            <option value="all">All dates</option>
            <option value="today">Today</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
          </select>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="px-3 py-2 text-gray-600 hover:text-gray-900 text-sm font-medium">Clear</button>
          )}
          <button
            onClick={refreshNews}
            disabled={newsLoading}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
          >
            <RefreshCw className={`h-4 w-4 ${newsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {((newsSourceTab === 'all' && (newsLoading || communityLoading)) || (newsSourceTab === 'community' && communityLoading) || (newsSourceTab === 'google' && newsLoading)) ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-10 w-10 animate-spin text-gray-400" />
              </div>
            ) : (newsSourceTab === 'google' && newsError) ? (
              <div className="py-8 text-center border border-red-200 rounded-lg bg-red-50">
                <p className="text-red-700 mb-4">{newsError}</p>
                <button onClick={refreshNews} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium">Try Again</button>
              </div>
            ) : (newsSourceTab === 'community' && communityError) ? (
              <div className="py-8 text-center border border-red-200 rounded-lg bg-red-50">
                <p className="text-red-700 mb-4">{communityError}</p>
                <button onClick={() => window.location.reload()} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium">Try Again</button>
              </div>
            ) : mergedList.length === 0 ? (
              <div className="py-16 text-center border border-gray-200 rounded-lg bg-gray-50">
                <Newspaper className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">
                  {hasActiveFilters ? 'No news matches your filters' : newsSourceTab === 'community' ? 'No community news yet' : newsSourceTab === 'google' ? 'No Google news available' : 'No news at the moment'}
                </p>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium">Clear filters</button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white overflow-hidden">
                {mergedList.map((item, index) => {
                  const textContent = String(item.description || '').replace(/<[^>]*>/g, '').trim();
                  const excerpt = textContent.length > 180 ? textContent.substring(0, 180) + '...' : textContent;
                  const articleUrl = item.url && item.url.startsWith('http') ? item.url : item.url ? `https://${item.url}` : null;
                  const imageUrl = item.image ? (item.image.startsWith('http') ? item.image : getImageUrl(item.image)) : null;
                  const dateStr = item.publishedAt || item.createdAt;
                  const RowContent = (
                    <>
                      <div className="shrink-0 w-14 text-sm text-gray-500 font-medium tabular-nums">{formatTimeOrDate(dateStr)}</div>
                      {imageUrl && (
                        <div className="shrink-0 w-24 h-16 rounded overflow-hidden bg-gray-100">
                          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        {item._source === 'community' && item.category && (
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-1.5 ${getCategoryBadgeClass(item.category)}`}>
                            {String(item.category)}
                          </span>
                        )}
                        <h3 className="font-bold text-gray-900 group-hover:text-gray-700 line-clamp-2">{item.title}</h3>
                        {excerpt && <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{excerpt}</p>}
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500" onClick={(e) => e.stopPropagation()}>
                          {item._source === 'community' && item._id ? (
                            <>
                              {user?._id ? (
                                <button
                                  type="button"
                                  onClick={(e) => handleLikeCommunityNews(e, item._id)}
                                  disabled={likingNewsId === item._id}
                                  className={`flex items-center gap-1 hover:text-gray-700 ${(item.likes || []).some((l: any) => String(l.user?._id || l.user) === String(user._id)) ? 'text-red-600' : ''}`}
                                >
                                  {likingNewsId === item._id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Heart className="h-3.5 w-3.5" fill={(item.likes || []).some((l: any) => String(l.user?._id || l.user) === String(user._id)) ? 'currentColor' : 'none'} />}
                                  {(item.likes || []).length}
                                </button>
                              ) : (
                                <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" />{(item.likes || []).length}</span>
                              )}
                              <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" />{(item.comments || []).length}</span>
                            </>
                          ) : articleUrl ? (
                            <>
                              {user?._id ? (
                                <button
                                  type="button"
                                  onClick={(e) => handleLikeArticle(e, articleUrl)}
                                  disabled={likingArticleUrl === articleUrl}
                                  className={`flex items-center gap-1 hover:text-gray-700 ${(articleEngagement[articleUrl]?.userLiked) ? 'text-red-600' : ''}`}
                                >
                                  {likingArticleUrl === articleUrl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Heart className="h-3.5 w-3.5" fill={(articleEngagement[articleUrl]?.userLiked) ? 'currentColor' : 'none'} />}
                                  {(articleEngagement[articleUrl]?.likes || []).length}
                                </button>
                              ) : (
                                <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" />{(articleEngagement[articleUrl]?.likes || []).length}</span>
                              )}
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCommentsModalItem({ ...item, url: articleUrl }); }}
                                className="flex items-center gap-1 hover:text-gray-700"
                              >
                                <MessageCircle className="h-3.5 w-3.5" />
                                {(articleEngagement[articleUrl]?.comments || []).length}
                              </button>
                            </>
                          ) : null}
                          <div className="relative group/share">
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShareMenuOpen(shareMenuOpen === (item._id || articleUrl) ? null : (item._id || articleUrl)); }}
                              className="flex items-center gap-1 hover:text-gray-700"
                            >
                              <Share2 className="h-3.5 w-3.5" /> Share
                            </button>
                            {shareMenuOpen === (item._id || articleUrl) && (
                              <div className="absolute left-0 top-full mt-1 py-1 bg-white rounded shadow-lg border z-20 min-w-[120px]">
                                <button type="button" onClick={(e) => shareNews(e, item, 'facebook')} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-2"><Facebook className="h-3.5 w-3.5" /> Facebook</button>
                                <button type="button" onClick={(e) => shareNews(e, item, 'twitter')} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-2"><Twitter className="h-3.5 w-3.5" /> Twitter</button>
                                <button type="button" onClick={(e) => shareNews(e, item, 'linkedin')} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-2"><Linkedin className="h-3.5 w-3.5" /> LinkedIn</button>
                                <button type="button" onClick={(e) => shareNews(e, item, 'email')} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> Email</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {articleUrl && <ExternalLink className="h-4 w-4 text-gray-400 shrink-0 group-hover:text-gray-600" />}
                      {item._source === 'community' && user && (user._id === (item.creator?._id || item.creator) || user.role?.toLowerCase() === 'admin') && (
                        <>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/news/edit/${item._id}`); }}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg shrink-0"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteNews(item._id); }}
                            disabled={deletingNewsId === item._id}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                            title="Delete"
                          >
                            {deletingNewsId === item._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        </>
                      )}
                    </>
                  );
                  const itemKey = item._id || item.url || `news-${index}`;
                  const isCommunity = item._source === 'community' && item._id;
                  if (isCommunity) {
                    return (
                      <Link key={itemKey} to={`/news/${item._id}`} className="group flex items-start gap-4 p-4 hover:bg-gray-50 transition-colors text-left">
                        {RowContent}
                      </Link>
                    );
                  }
                  if (articleUrl) {
                    return (
                      <a key={itemKey} href={articleUrl} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 p-4 hover:bg-gray-50 transition-colors text-left">
                        {RowContent}
                      </a>
                    );
                  }
                  return (
                    <div key={itemKey} className="group flex items-start gap-4 p-4 hover:bg-gray-50 transition-colors">
                      {RowContent}
                    </div>
                  );
                })}
              </div>
            )}

            {mergedList.length > 0 && (
              <div className="mt-8 text-center">
                <a
                  href="https://news.google.com/search?q=agriculture+farming"
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
                >
                  View more news
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>

          <aside className="lg:col-span-1 space-y-6">
            <div className="border border-gray-200 rounded-lg bg-white p-5">
              <h3 className="font-bold text-gray-900 mb-4">Most read</h3>
              <ul className="space-y-3">
                {mostReadList.slice(0, 6).map((item, index) => {
                  const articleUrl = item.url && item.url.startsWith('http') ? item.url : item.url ? `https://${item.url}` : null;
                  const isCommunity = item._source === 'community' && item._id;
                  return (
                    <li key={item._id || item.url || `mostread-${index}`}>
                      {isCommunity ? (
                        <Link to={`/news/${item._id}`} className="text-sm font-medium text-gray-900 hover:text-gray-600 line-clamp-2">
                          {item.title}
                        </Link>
                      ) : articleUrl ? (
                        <a href={articleUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-gray-900 hover:text-gray-600 line-clamp-2">
                          {item.title}
                        </a>
                      ) : (
                        <span className="text-sm font-medium text-gray-900 line-clamp-2">{item.title}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg bg-gray-50 p-5">
              <h3 className="font-bold text-gray-900 mb-2">Newsletter</h3>
              <p className="text-sm text-gray-600 mb-4">
                Subscribe to receive the top articles of the week
              </p>
              <form onSubmit={handleNewsletterSubscribe} className="space-y-2">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                />
                <button
                  type="submit"
                  disabled={newsletterSubscribing}
                  className="w-full px-3 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {newsletterSubscribing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Subscribe
                </button>
              </form>
              {newsletterSuccess && <p className="text-sm text-green-600 mt-2">Subscription successful!</p>}
              {newsletterError && <p className="text-sm text-red-600 mt-2">{newsletterError}</p>}
            </div>
          </aside>
        </div>

        {commentsModalItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setCommentsModalItem(null)}>
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-semibold text-gray-900 line-clamp-1">{commentsModalItem.title}</h3>
                <button onClick={() => setCommentsModalItem(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <p className="text-sm text-gray-600 mb-4">
                  {(articleEngagement[commentsModalItem.url]?.comments || []).length} Comments
                </p>
                {user?._id ? (
                  <form onSubmit={handleAddArticleComment} className="mb-4">
                    <textarea
                      value={articleCommentText}
                      onChange={(e) => setArticleCommentText(e.target.value)}
                      placeholder="Write a comment..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      maxLength={1000}
                    />
                    <button type="submit" disabled={!articleCommentText.trim() || submittingArticleComment} className="mt-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                      {submittingArticleComment ? <Loader2 className="h-4 w-4 animate-spin inline" /> : null} Post
                    </button>
                  </form>
                ) : (
                  <p className="text-sm text-gray-500 mb-4">
                    <Link to="/login" className="text-indigo-600 hover:underline">Log in</Link> to add a comment
                  </p>
                )}
                <ul className="space-y-3">
                  {(articleEngagement[commentsModalItem.url]?.comments || []).map((c: any) => (
                    <li key={c._id} className="flex gap-2">
                      <div className="flex-1 bg-gray-50 rounded-lg p-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900 text-sm">{c.user?.name || 'User'}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{c.createdAt && new Date(c.createdAt).toLocaleDateString()}</span>
                            {(user?._id === (c.user?._id || c.user) || user?.role?.toLowerCase() === 'admin') && (
                              <button onClick={() => handleDeleteArticleComment(c._id)} className="text-xs text-red-600 hover:underline">Delete</button>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-700 text-sm mt-0.5">{c.content}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
